import styled from '@emotion/styled'
import { metrics, colors, fonts, zIndex } from 'component-library/__styling/settings'
import { withClass } from 'common/emotion'
import { themedValue } from 'component-library/__styling/themed-value'
import { conditionalStyles } from 'component-library/__styling/conditional-styles'
import { calcRem, breakpoint } from 'component-library/__styling/style-functions'
import { visuallyhidden, transition } from 'component-library/__styling/style-mixins'
import { IconCross } from 'component-library/icons/IconCross/IconCross'

export const StyledNav = withClass('hide-print')(
    styled('nav')(({ theme }) => ({
        ...conditionalStyles(theme.kind === 'sevennews', {
            maxWidth: '100%',
            display: 'flex',
            justifyContent: 'center',
            fontWeight: 600,
            fontFamily: fonts.sevennews.sansSerif,
            textTransform: 'uppercase',
        }),
    }))
)

const closeButtonSize = 48
export const StyledNavWrapperClass = 'stylednavwrapper'

export const StyledNavWrapper = withClass(StyledNavWrapperClass)(
    styled('div')(
        {
            position: 'fixed',
            width: `calc(100% - ${calcRem(closeButtonSize)})`,
            maxWidth: 375,
            height: '100vh',
            padding: 0,
            paddingTop: calcRem(closeButtonSize),
            margin: 0,
            fontFamily: fonts.thewest.sansSerifCond,
            top: 0,
            zIndex: zIndex.thewest.flyoutNav,
            backgroundColor: colors.thewest.greyGorilla,
            color: colors.white,
            ...transition({ properties: ['transform'], speed: 'slow' }),
        },
        ({ theme }) => ({
            ...conditionalStyles(theme.kind === 'sevennews', {
                maxWidth: metrics.sevennews.mainContentWidth,
                minWidth: 0, // Prevent defaulting to auto. In FF auto means scale to contents making the nav too wide on mobile.
                width: 1,
                display: 'flex',
                // safari has issue if we don't manually force the width of this container to grow, it will
                // not shrink the nav responsively. The hack here is to give it grow rule with an initial
                // width of 1
                flexGrow: 1,
                flexBasis: '100%',
                // this is so we get the effect that the nav sits at the site width - so we can still have
                // effective shadows for the scroll // minus one padding size for shadow
                position: 'relative',
            }),
            ...conditionalStyles(theme.kind === 'thewest', {
                display: 'none',
                transform: `translateX(calc(-100% - ${closeButtonSize}px))`,

                '&.isOpen': {
                    display: 'flex',
                    flexDirection: 'column',
                },

                '&.isTransitioning': {
                    transform: 'translateX(0)',
                },
            }),
        })
    )
)

export const StyledCloseButton = styled('button')({
    height: calcRem(closeButtonSize),
    width: calcRem(closeButtonSize),
    margin: 0,
    padding: 0,
    position: 'absolute',
    top: 0,
    right: calcRem(-closeButtonSize),
    lineHeight: 1,
    border: '0',
    cursor: 'pointer',
    backgroundColor: colors.thewest.greyGorilla,
    color: colors.white,
    ...transition({ properties: ['background-color'] }),
    outline: 'none',

    '&:hover, &:focus': {
        backgroundColor: colors.thewest.greyElephant,
    },
})

export const StyledIconCross = styled(IconCross)({
    height: calcRem(12),
    width: calcRem(12),
    fill: colors.white,
})

export const StyledButtonText = styled('span')(visuallyhidden)

export const StyledNavList = styled('ul')(
    {
        margin: calcRem(0, 0, 90, 0), // large bottom margin so last nav list items are not covered by menu in Safari on iOS
        display: 'block',
        width: '100%',
        flexGrow: 1,
        padding: calcRem(0, 4, 15, 13),
        overflowY: 'scroll',
        MsOverflowStyle: '-ms-autohiding-scrollbar',

        '&::-webkit-scrollbar': {
            width: 9,
            backgroundColor: colors.thewest.greyGorilla,
        },

        '&::-webkit-scrollbar-track': {
            width: 9,
        },

        '&::-webkit-scrollbar-thumb': {
            backgroundColor: colors.thewest.greyElephant,
            borderRadius: 10,
        },
        [breakpoint('sm')]: {
            margin: 0,
        },
    },

    ({ theme }) => ({
        backgroundColor: themedValue(theme, {
            sevennews: section => section.mobileNav.primaryColor,
            fallback: undefined,
        }),
        color: themedValue(theme, {
            sevennews: section => section.mobileNav.textColor,
            fallback: undefined,
        }),

        ...conditionalStyles(theme.kind === 'sevennews', {
            fontFamily: fonts.sevennews.sansSerifCond,
            zIndex: zIndex.sevennews.flyoutNav,
            '& span': {
                width: '100%',
            },
        }),
    })
)
StyledNavList.displayName = 'StyledNavList'
